import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { App } from './app/App';
import { AppErrorBoundary } from './app/AppErrorBoundary';
import { ClientProvider } from './data/ClientProvider';
import type { AppClient } from './data/client';
import { AppHttpClient } from './data/http/AppHttpClient';
import { queryKeys } from './data/queryKeys';
import './index.css';
import { applyGlassPath, TOOLTIP_DELAY, ToastProvider, TooltipProvider } from './ui';

/**
 * INV-13: TanStack Query is the only cache in this application. One client,
 * created here, provided once.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A CRM is read-heavy and long-lived in a tab; refetching on every focus
      // is noise. Freshness comes from SSE invalidation (data/live/).
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/**
 * WHICH RECORDS A FIXTURE BUILD SERVES (package GX-SPEED): the review states by default, the
 * live instance's volume with `VITE_FIXTURE_DATASET=live-size` (`data/fixtures/live-size.ts`),
 * which the speed check builds and measures; only that build counts its client calls
 * (`data/fixtures/call-log.ts`). `VITE_FIXTURES=1` stays the one switch to the fixture build.
 */
const FIXTURE_DATASET =
  (import.meta.env as { readonly VITE_FIXTURE_DATASET?: string }).VITE_FIXTURE_DATASET ===
  'live-size'
    ? 'live-size'
    : 'review';

/**
 * THE swap point. Normally the real API client; in a REVIEW BUILD
 * (`VITE_FIXTURES=1`, see app/reviewMode.ts) the FixtureClient, so the app runs
 * with no server behind it. Nothing else in the app knows the difference - which is
 * the property that makes the fixture path worth having at all.
 *
 * `onUnauthenticated` is how an EXPIRED session becomes a redirect: any 401 writes `null`
 * into the session entry, the guard sees it on the next render and sends the user to the
 * login screen with the path they were on. One place, instead of a check per call site.
 *
 * `latencyMs` on the fixture path is deliberate: at 0 every skeleton and every
 * optimistic write is invisible, and those states are half of what a design review
 * has to judge.
 */
// Direct dot access lets Vite replace an absent flag with undefined before bundling.
const client: AppClient =
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? await fixtureClient()
    : new AppHttpClient({
        onUnauthenticated: () => {
          queryClient.setQueryData(queryKeys.session(), null);
        },
      });

async function fixtureClient(): Promise<AppClient> {
  const fixture = new (await import('./data/fixtures/FixtureClient')).FixtureClient({
    latencyMs: 140,
    denseSeed: true,
    identitySeed: true,
    dataset: FIXTURE_DATASET,
    /**
     * `?as=owner` signs the REVIEW BUILD in as the Owner (wave 5, D-F3.3). The fixture
     * session holds `Operator`, which covers every principal, so the dimmed state of a
     * row the reader may not touch has no way of appearing without this. Fixture-only:
     * the real client takes its viewer from the session and nothing else.
     */
    ...(new URLSearchParams(window.location.search).get('as') === 'owner'
      ? { viewerRole: 'owner' as const }
      : {}),
  });
  return FIXTURE_DATASET === 'live-size'
    ? (await import('./data/fixtures/call-log')).withFixtureCallLog(fixture)
    : fixture;
}

/**
 * WHICH GLASS THE ENGINE GETS, decided before the first paint so no surface renders frosted
 * and then jumps to refracted (`ui/glassPath.ts` carries the reasoning and the measurement).
 */
applyGlassPath();

const container = document.getElementById('root');
if (container === null) {
  throw new Error('index.html is missing the #root element');
}

createRoot(container).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ClientProvider client={client}>
          <TooltipProvider delayDuration={TOOLTIP_DELAY}>
            <ToastProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ToastProvider>
          </TooltipProvider>
        </ClientProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
